'use strict';

// Central registry of config-driven workspaces built on pict-section-workspace. App-Web calls registerWorkspaces()
// (registers each provider+view pair); the router calls registerWorkspaceRoutes() (the GUID-bridge + :Tab routes).
// Hand-built workspaces (Project/Contract/User/Asset/Sample) migrate into this list as they are converted.

const libWorkspace = require('pict-section-workspace');
const libWorkspaceReturnRoutes = require('./Workspace-ReturnRoutes.js');

const CONFIGS =
[
	// Tier 1 — new operational-entity workspaces
	require('./Organization.config.js'),
	require('./Material.config.js'),
	require('./PayItem.config.js'),
	require('./Test.config.js'),
	require('./MixDesign.config.js'),
	require('./MixType.config.js'),
	require('./Product.config.js'),
	// Tier 2
	require('./Lab.config.js'),
	require('./Equipment.config.js'),
	require('./Location.config.js'),
	require('./Customer.config.js'),
	// New architecture — Teams (people-manager enrollment; access via the DocumentAccessJoin projection engine)
	require('./Team.config.js'),
	// New architecture — Inspections (per-record workspace over the Inspection* Meadow joins; Bridge-app replacement)
	require('./Inspection.config.js'),
	// Read-only (field / approval data)
	require('./Observation.config.js'),
	require('./Document.config.js'),
	// Financial entities
	require('./Fund.config.js'),
	require('./FundingCategory.config.js'),
	require('./Account.config.js'),
	require('./AccountType.config.js'),
	require('./FundingSource.config.js'),
	// Organization sub-entities
	require('./ProductionPlant.config.js'),
	require('./Contact.config.js'),
	require('./ProjectOffice.config.js'),
	// Fulfillment
	require('./Lot.config.js'),
	// Migrated existing workspaces (were hand-built views/providers; now config + a bespoke-methods subclass)
	require('./Contract.config.js'),
	require('./Asset.config.js'),
	require('./AssetType.config.js'),
	require('./User.config.js'),
	require('./Project.config.js'),
	require('./Sample.config.js'),
];

const _ReturnRoutes = new libWorkspaceReturnRoutes();

function registerWorkspaces(pPict)
{
	// pict-section-workspace (>=1.0.1) exposes app-wide config hooks; wire the three things this app needs once,
	// up front, so no per-config plumbing is required:
	//  - the mount point (the published default `#Pict-Application-Container` does not exist in this app);
	//  - the record REST client (the published default `RestClient` is a generic client with no `/1.0/` base URL;
	//    HeadlightRestClient prepends it — otherwise reads hit `/<Entity>/:id` and 404);
	//  - the association editor (browserify drops the library's optional computed require, so `assoc` tabs need the
	//    RecordSet editor injected explicitly).
	if (typeof libWorkspace.setDefaultDestination === 'function') { libWorkspace.setDefaultDestination('#PlatformConfiguration-Application-Container'); }
	if (typeof libWorkspace.setDefaultRestClientName === 'function') { libWorkspace.setDefaultRestClientName('HeadlightRestClient'); }
	if (typeof libWorkspace.setAssociationEditorLib === 'function') { libWorkspace.setAssociationEditorLib(require('pict-section-recordset/source/views/associate/RecordSet-AssociationEditor.js')); }
	for (const tmpConfig of CONFIGS) { libWorkspace.createWorkspace(pPict, tmpConfig); }
	// Each workspace's Back link returns to the list (or dashboard) its record was opened from, filters and all.
	if (_ReturnRoutes.entries.length < 1)
	{
		for (const tmpConfig of CONFIGS)
		{
			_ReturnRoutes.applyTo(tmpConfig);
		}
	}
	if (typeof window !== 'undefined')
	{
		_ReturnRoutes.install(window);
	}
}

function registerWorkspaceRoutes(pRouter)
{
	for (const tmpConfig of CONFIGS) { libWorkspace.registerWorkspaceRoutes(pRouter, tmpConfig); }
}

/**
 * Note a move the router makes without a hashchange (PictRouter.navigate pushes history), so a workspace opened that
 * way still gets a Back link to where it was opened from.
 *
 * @param {string} pFromHash - the current location hash
 * @param {string} pToRoute - the route about to be navigated to, with or without its leading '#'
 */
function noteRouteChange(pFromHash, pToRoute)
{
	const tmpTo = String(pToRoute || '');
	_ReturnRoutes.noteRouteChange(pFromHash || '', (tmpTo.charAt(0) === '#') ? tmpTo : `#${tmpTo}`);
}

module.exports = { CONFIGS, registerWorkspaces, registerWorkspaceRoutes, noteRouteChange };
